<script setup lang="ts">
import { computed } from 'vue'
import { useOrgAssignmentStore } from '@/stores/orgAssignment'

defineEmits<{ prev: []; next: [] }>()

const orgAssignmentStore = useOrgAssignmentStore()
const org = computed(() => orgAssignmentStore.orgAssignment)

function display(value: string | undefined | null): string {
  return value?.trim() || '—'
}
</script>

<template>
  <div class="max-w-[900px] mx-auto px-7 py-[22px] pb-16 flex flex-col gap-[18px]">
    <!-- Section head -->
    <div class="flex items-end gap-3.5 flex-wrap">
      <div class="flex flex-col gap-0.5">
        <span class="font-mono text-[11px] tracking-[0.12em] uppercase text-primary">ICS 207 · Auto-derived</span>
        <h2 class="font-headline font-black text-[26px] uppercase tracking-tight text-on-surface m-0">Organization Chart</h2>
      </div>
      <p class="flex-1 min-w-[200px] text-[13px] text-outline m-0">Visual org chart derived automatically from ICS 203. Read-only.</p>
    </div>

    <!-- Org chart group -->
    <div class="bg-surface border border-border rounded flex flex-col">
      <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
        <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Organizational Chart</h3>
        <span class="ml-auto font-mono text-[9px] tracking-[0.08em] uppercase text-outline px-1.5 py-0.5 border border-border rounded bg-surface-container-low">Derived from ICS 203</span>
      </div>

      <div v-if="!org" class="font-body text-[13px] text-outline py-8 text-center">
        Fill in the ICS 203 section to populate this chart.
      </div>

      <div v-else class="overflow-x-auto bg-surface-container-low p-[18px] rounded-b">
        <div class="flex flex-col items-center gap-0 min-w-[600px]">
          <!-- IC -->
          <div class="bg-primary border border-primary text-white rounded px-3 py-2 text-center min-w-[160px]">
            <p class="font-mono text-[9px] tracking-[0.10em] uppercase opacity-80 mb-0.5">Incident Commander</p>
            <p class="font-headline font-bold text-[13px]" :class="{ 'italic opacity-60': !org.incident_commander?.name }">{{ display(org.incident_commander?.name) }}</p>
          </div>
          <div class="w-px h-[18px] bg-border" />

          <!-- Search Manager -->
          <div class="bg-surface border border-border rounded px-3 py-2 text-center min-w-[160px]">
            <p class="font-mono text-[9px] tracking-[0.10em] uppercase text-outline mb-0.5">Search Manager</p>
            <p class="font-headline font-bold text-[13px] text-on-surface" :class="{ 'italic font-normal text-outline': !org.search_manager?.name }">{{ display(org.search_manager?.name) }}</p>
          </div>
          <div class="w-full h-px bg-border my-4" />

          <!-- Staff Row -->
          <div class="grid grid-cols-3 gap-2.5 w-full mb-4">
            <div v-for="(cfg, i) in [
              { label: 'PIO', val: org.pio },
              { label: 'Liaison', val: org.liaison },
              { label: 'Safety Officer', val: org.safety_officer },
            ]" :key="i" class="bg-surface border border-border rounded px-3 py-2 text-center">
              <p class="font-mono text-[9px] tracking-[0.10em] uppercase text-outline mb-0.5">{{ cfg.label }}</p>
              <p class="font-headline font-bold text-[13px] text-on-surface" :class="{ 'italic font-normal text-outline': !cfg.val?.trim() }">{{ display(cfg.val) }}</p>
            </div>
          </div>

          <div class="w-full h-px bg-border my-4" />

          <!-- Section Chiefs -->
          <div class="grid grid-cols-4 gap-2.5 w-full">
            <div class="flex flex-col gap-1.5">
              <div class="bg-surface-container-high border border-border rounded px-3 py-2 text-center">
                <p class="font-mono text-[9px] tracking-[0.10em] uppercase text-outline mb-0.5">Plans Chief</p>
                <p class="font-headline font-bold text-[13px] text-on-surface" :class="{ 'italic font-normal text-outline': !org.plans_chief?.trim() }">{{ display(org.plans_chief) }}</p>
              </div>
              <div v-if="org.plans_deputy" class="bg-surface border border-border rounded px-3 py-2 text-center">
                <p class="font-mono text-[9px] tracking-[0.10em] uppercase text-outline mb-0.5">Deputy</p>
                <p class="font-body text-[13px] text-on-surface">{{ org.plans_deputy }}</p>
              </div>
            </div>
            <div class="flex flex-col gap-1.5">
              <div class="bg-surface-container-high border border-border rounded px-3 py-2 text-center">
                <p class="font-mono text-[9px] tracking-[0.10em] uppercase text-outline mb-0.5">Operations Chief</p>
                <p class="font-headline font-bold text-[13px] text-on-surface" :class="{ 'italic font-normal text-outline': !org.ops_chief?.trim() }">{{ display(org.ops_chief) }}</p>
              </div>
              <div v-if="org.ops_deputy" class="bg-surface border border-border rounded px-3 py-2 text-center">
                <p class="font-mono text-[9px] tracking-[0.10em] uppercase text-outline mb-0.5">Deputy</p>
                <p class="font-body text-[13px] text-on-surface">{{ org.ops_deputy }}</p>
              </div>
              <div v-for="(div, i) in (org.divisions ?? [])" :key="i" class="bg-surface border border-border rounded px-3 py-2 text-center ml-2">
                <p class="font-mono text-[9px] tracking-[0.10em] uppercase text-outline mb-0.5">{{ div.label || `Div ${i + 1}` }}</p>
                <p class="font-body text-[13px] text-on-surface" :class="{ 'italic text-outline': !div.supervisor?.trim() }">{{ display(div.supervisor) }}</p>
              </div>
            </div>
            <div class="flex flex-col gap-1.5">
              <div class="bg-surface-container-high border border-border rounded px-3 py-2 text-center">
                <p class="font-mono text-[9px] tracking-[0.10em] uppercase text-outline mb-0.5">Logistics Chief</p>
                <p class="font-headline font-bold text-[13px] text-on-surface" :class="{ 'italic font-normal text-outline': !org.logistics_chief?.trim() }">{{ display(org.logistics_chief) }}</p>
              </div>
              <div v-if="org.logistics_deputy" class="bg-surface border border-border rounded px-3 py-2 text-center">
                <p class="font-mono text-[9px] tracking-[0.10em] uppercase text-outline mb-0.5">Deputy</p>
                <p class="font-body text-[13px] text-on-surface">{{ org.logistics_deputy }}</p>
              </div>
              <div class="bg-surface border border-border rounded px-3 py-2 text-center">
                <p class="font-mono text-[9px] tracking-[0.10em] uppercase text-outline mb-0.5">Comms</p>
                <p class="font-body text-[13px] text-on-surface" :class="{ 'italic text-outline': !org.comms?.trim() }">{{ display(org.comms) }}</p>
              </div>
            </div>
            <div>
              <div class="bg-surface-container-high border border-border rounded px-3 py-2 text-center">
                <p class="font-mono text-[9px] tracking-[0.10em] uppercase text-outline mb-0.5">Intel Chief</p>
                <p class="font-headline font-bold text-[13px] text-on-surface" :class="{ 'italic font-normal text-outline': !org.intel_chief?.trim() }">{{ display(org.intel_chief) }}</p>
              </div>
            </div>
          </div>

          <!-- Agency Reps -->
          <div v-if="org.agency_reps?.length > 0" class="w-full mt-6 border-t border-border pt-4">
            <p class="font-mono text-[10px] tracking-[0.12em] uppercase text-outline mb-2">Agency Representatives</p>
            <div class="flex flex-wrap gap-2">
              <div v-for="(rep, i) in org.agency_reps" :key="i" class="bg-surface border border-border rounded px-3 py-2 text-center min-w-[120px]">
                <p class="font-body text-[13px] text-on-surface">{{ display(rep.name) }}</p>
                <p v-if="rep.agency" class="font-mono text-[10px] text-outline">{{ rep.agency }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Section footer -->
    <div class="flex items-center gap-2.5 mt-2 pt-3 border-t border-border/60">
      <button class="inline-flex items-center gap-1.5 bg-surface border border-border rounded px-3.5 py-2 font-mono font-medium text-[11px] tracking-[0.10em] uppercase text-on-surface cursor-pointer hover:bg-surface-container-low transition-colors" @click="$emit('prev')">
        <span class="material-symbols-outlined text-[14px]">arrow_back</span>
        ICS 206
      </button>
      <span class="flex-1" />
      <button class="inline-flex items-center gap-1.5 bg-primary border border-primary rounded px-3.5 py-2 font-mono font-medium text-[11px] tracking-[0.10em] uppercase text-white cursor-pointer hover:opacity-90 transition-opacity" @click="$emit('next')">
        ICS 208
        <span class="material-symbols-outlined text-[14px]">arrow_forward</span>
      </button>
    </div>
  </div>
</template>
